import { Response, apiAbortController } from '@ui/src';
import api from '../../../../api/api';
import {
  PSPDetailsKeys,
  PSPDetails,
  PSPValidator,
  RateSource,
  TransactionsSettingsKeys,
  PSPRatesKeys,
  PSPWebhooksKeys,
  PSPAuthorizationKeys,
  PSPBrandKeys,
  PSPLanguangesKeys,
  PSPOTPKeys,
  CASPAlias,
  CASPAliasFormValues,
  AMLBotAssets,
} from '../../types';

export const pspDetailsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPSPDetails: build.query<Response<PSPDetails>, void>({
      query: () => ({
        url: `/v1/settings?take=9999`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRateSources: build.query<Response<RateSource>, void>({
      query: () => ({
        url: '/v1/rates-sources?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPSPDetailsValidators: build.query<{ data: PSPValidator }, void>({
      query: () => ({
        url: '/v1/settings/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCASPAliases: build.query<Response<CASPAlias>, void>({
      query: () => ({
        url: '/v1/casp-aliases',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAMLBotAssets: build.query<{ data: { modesAssets: AMLBotAssets } }, void>(
      {
        query: () => ({
          url: '/v1/settings/aml-bot-assets',
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    createCASPAlias: build.mutation<{ data: CASPAlias }, CASPAliasFormValues>({
      query: (body) => ({
        url: '/v1/casp-aliases',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCASPAlias: build.mutation<{ data: CASPAlias }, string>({
      query: (id) => ({
        url: `/v1/casp-aliases/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editPSPDetails: build.mutation<
      { data: PSPDetails },
      {
        [key: string]: any;
        key:
          | PSPDetailsKeys
          | PSPRatesKeys
          | PSPWebhooksKeys
          | PSPAuthorizationKeys
          | PSPBrandKeys
          | PSPLanguangesKeys
          | PSPOTPKeys;
      }
    >({
      query: (data) => ({
        url: '/v1/settings',
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editBeneficiarySettings: build.mutation<
      { data: PSPDetails },
      {
        [key: string]: any;
        key: TransactionsSettingsKeys;
      }
    >({
      query: (data) => ({
        url: `/v1/settings`,
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetPSPDetailsQuery,
  useLazyGetPSPDetailsQuery,
  useGetPSPDetailsValidatorsQuery,
  useLazyGetPSPDetailsValidatorsQuery,
  useEditPSPDetailsMutation,
  useLazyGetRateSourcesQuery,
  useEditBeneficiarySettingsMutation,
  useLazyGetCASPAliasesQuery,
  useCreateCASPAliasMutation,
  useDeleteCASPAliasMutation,
  useLazyGetAMLBotAssetsQuery,
} = pspDetailsApi;

export const {
  endpoints: {
    getPSPDetails,
    getPSPDetailsValidators,
    getRateSources,
    editBeneficiarySettings,
    getCASPAliases,
    createCASPAlias,
    deleteCASPAlias,
    getAMLBotAssets,
  },
} = pspDetailsApi;
